import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Chip,
  Autocomplete,
  Table,
  TableRow,
  TableCell,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import AppConfig from "appConfig";
import usePageTitle from "app/pageTitle";

export default function OpsEntityOveriewPage() {
  const title = "Business Entities";
  usePageTitle(title);

  const { ref } = useParams();
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [entityOverview, setEntityOverview] = useState(null);
  const [resultData, setResultData] = useState({});

  function singularize(word) {
    if (!word || typeof word !== "string") return word;

    const irregular = {
      people: "person",
      men: "man",
      women: "woman",
      children: "child",
      teeth: "tooth",
      feet: "foot",
      mice: "mouse",
      geese: "goose",
      data: "datum",
      indices: "index",
      analyses: "analysis",
      crises: "crisis",
      diagnoses: "diagnosis",
      theses: "thesis",
      lives: "life",
      wives: "wife",
      knives: "knife",
      leaves: "leaf",
      shelves: "shelf",
      wolves: "wolf",
    };

    const lower = word.toLowerCase();

    if (irregular[lower]) {
      return matchCase(word, irregular[lower]);
    }

    if (lower.endsWith("ies") && lower.length > 3) {
      return matchCase(word, lower.slice(0, -3) + "y");
    }

    if (/(xes|zes|ches|shes|sses)$/.test(lower)) {
      return matchCase(word, lower.slice(0, -2));
    }

    if (lower.endsWith("ves")) {
      return matchCase(word, lower.slice(0, -3) + "f");
    }

    if (lower.endsWith("s") && !lower.endsWith("ss")) {
      return matchCase(word, lower.slice(0, -1));
    }

    return word;
  }

  function matchCase(original, replacement) {
    return original[0] === original[0].toUpperCase()
      ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
      : replacement;
  }

  const formatKoboToNaira = (amountInKobo) => {
    const nairaAmount = (Number(amountInKobo) || 0) / 100;
    return `${resultData?.currency}${nairaAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  async function getEntityReport() {
    setShowLoading(true);
    await axios
      .get(`/portal/ops/relationships/entities/${ref}/overview.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setResultData(result.data);
          setEntityOverview(result.data.data.overview);

          setPageLoaded(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  useEffect(() => {
    getEntityReport();
  }, [refreshPage]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox mb={5}>
        {pageLoaded && (
          <MDBox>
            <MDBox mt={1}>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  window.history.back();
                }}
              >
                Back
              </Button>
            </MDBox>
            <MDBox
              mt={1}
              p={2}
              mb={3}
              borderRadius="lg"
              sx={{
                background: "#fafafa",
                border: "1px solid #ececec",
              }}
            >
              <MDTypography variant="caption" color="text">
                {singularize(entityOverview.relationship).toUpperCase()}
              </MDTypography>
              <MDTypography variant="h5" fontWeight="bold">
                {entityOverview.title}
              </MDTypography>

              <MDTypography mt={3} variant="body2" color="text" fontSize={13}>
                ASSIGNED TO
              </MDTypography>
              <MDBox display="flex" alignItems="center">
                <Icon
                  sx={{
                    fontSize: 18,
                    color: "#757575",
                    mr: 1,
                  }}
                >
                  person
                </Icon>

                <MDTypography variant="button" color="text" fontWeight="regular">
                  {entityOverview.assigned_to}
                </MDTypography>
              </MDBox>
            </MDBox>

            <Card>
              <MDBox p={2}>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  textTransform="uppercase"
                  color="text"
                >
                  Check-In Report
                </MDTypography>
                <Grid container spacing={2} mt={0.5} mb={3}>
                  <Grid item xs={6}>
                    <MDBox
                      p={2}
                      borderRadius="lg"
                      sx={{
                        border: "1px solid #ececec",
                        textAlign: "center",
                      }}
                    >
                      <Icon fontSize="small">tour</Icon>

                      <MDTypography mt={1} variant="h6" fontWeight="bold" fontSize={24}>
                        {entityOverview.visits.count}
                      </MDTypography>

                      <MDTypography variant="caption" color="text">
                        Check Ins
                      </MDTypography>
                    </MDBox>
                  </Grid>

                  <Grid item xs={6}>
                    <MDBox
                      p={2}
                      borderRadius="lg"
                      sx={{
                        border: "1px solid #ececec",
                        textAlign: "center",
                      }}
                    >
                      <Icon fontSize="small">schedule</Icon>

                      <MDTypography mt={1} variant="h6" fontWeight="bold" fontSize={24}>
                        {entityOverview.visits.last ?? "---"}
                      </MDTypography>

                      <MDTypography variant="caption" color="text">
                        Last Check In
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            <MDBox mt={3}>
              <Card>
                <MDBox p={2}>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="uppercase"
                    color="text"
                  >
                    Activities Report
                  </MDTypography>

                  <MDBox mt={2}>
                    {entityOverview.activities.length > 0 ? (
                      <>
                        {entityOverview.activities.map((activity) => (
                          <MDBox
                            key={activity.activity}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            py={1.5}
                            px={2}
                            mb={1}
                            borderRadius="md"
                            sx={{
                              border: "1px solid #ececec",
                            }}
                          >
                            <MDBox>
                              <MDBox display="flex" alignItems="center">
                                <Icon
                                  sx={{
                                    fontSize: 18,
                                    mr: 1.5,
                                    color: "#757575",
                                  }}
                                >
                                  assignment
                                </Icon>

                                <MDTypography variant="button">{activity.activity}</MDTypography>
                              </MDBox>
                              <MDBox mt={1}>
                                <MDButton
                                  onClick={() =>
                                    navigate(
                                      `/ops/activities/${activity.reference}/entity/${ref}/logs`
                                    )
                                  }
                                  variant="contained"
                                  size="small"
                                  color="dark"
                                  startIcon={<Icon>list</Icon>}
                                >
                                  View Logs
                                </MDButton>
                              </MDBox>
                            </MDBox>

                            <Chip label={activity.count} size="small" />
                          </MDBox>
                        ))}
                      </>
                    ) : (
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        py={1.5}
                        px={2}
                        mb={1}
                        borderRadius="md"
                        sx={{
                          border: "1px solid #ececec",
                        }}
                      >
                        <MDTypography variant="button">No activity done yet</MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>

            <MDBox mt={3}>
              <Card>
                <MDBox p={2}>
                  <MDBox display="flex" justifyContent="space-between">
                    <MDTypography
                      variant="button"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="text"
                    >
                      Sales Report
                    </MDTypography>
                    <MDButton
                      color="dark"
                      variant="contained"
                      size="small"
                      startIcon={<Icon>list</Icon>}
                      onClick={() => {
                        navigate(`/ops/sales/entity/${ref}`);
                      }}
                    >
                      View Sales
                    </MDButton>
                  </MDBox>

                  <MDBox mt={2} mb={3}>
                    {entityOverview.sales.sales.length > 0 ? (
                      <MDBox>
                        <MDBox>
                          <Grid container spacing={2} mt={0.5} mb={3}>
                            <Grid item xs={6}>
                              <MDBox
                                p={2}
                                borderRadius="lg"
                                sx={{
                                  border: "1px solid #ececec",
                                  textAlign: "center",
                                }}
                              >
                                <Icon fontSize="small">payment</Icon>

                                <MDTypography mt={1} variant="h6" fontWeight="bold" fontSize={24}>
                                  {formatKoboToNaira(entityOverview.sales.vol)}
                                </MDTypography>

                                <MDTypography variant="caption" color="text">
                                  Volume
                                </MDTypography>
                              </MDBox>
                            </Grid>

                            <Grid item xs={6}>
                              <MDBox
                                p={2}
                                borderRadius="lg"
                                sx={{
                                  border: "1px solid #ececec",
                                  textAlign: "center",
                                }}
                              >
                                <Icon fontSize="small">checklist</Icon>

                                <MDTypography mt={1} variant="h6" fontWeight="bold" fontSize={24}>
                                  {entityOverview.sales.count}
                                </MDTypography>

                                <MDTypography variant="caption" color="text">
                                  Sales
                                </MDTypography>
                              </MDBox>
                            </Grid>
                          </Grid>
                        </MDBox>
                        <MDBox mt={3}>
                          <Table>
                            <TableRow>
                              <TableCell>
                                <MDTypography variant="h6" fontWeight="regular">
                                  Sales Rule
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="h6" fontWeight="regular">
                                  Sales
                                </MDTypography>
                              </TableCell>
                              <TableCell align="right">
                                <MDTypography variant="h6" fontWeight="regular">
                                  Volume
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                            {entityOverview.sales.sales.map((item) => (
                              <TableRow>
                                <TableCell>{item.rule}</TableCell>
                                <TableCell>{item.count}</TableCell>
                                <TableCell align="right">{formatKoboToNaira(item.vol)}</TableCell>
                              </TableRow>
                            ))}
                          </Table>
                        </MDBox>
                      </MDBox>
                    ) : (
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        py={1.5}
                        px={2}
                        mb={1}
                        borderRadius="md"
                        sx={{
                          border: "1px solid #ececec",
                        }}
                      >
                        <MDTypography variant="button">No sales done yet</MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
